import { useEffect, useState } from 'react';
import { Loader2, Lock, Printer, Unlock } from 'lucide-react';
import { getTallyOverview, finalizeMonth, reopenMonth } from '@generated/server/tally';
import { getStaffPass } from '@generated/hooks/staffPass';
import { money } from '@generated/components/pricing';
import { RHSR_LOGO, VSR_LOGO } from '@generated/components/AdGuide';
import { fmtPages } from '@generated/components/pages';

type Row = Awaited<ReturnType<typeof getTallyOverview>>['rows'][number];
const th = 'border-b-2 border-foreground px-2 py-2 text-left text-xs uppercase tracking-wider';
const td = 'border-b border-border px-2 py-2.5 tabular-nums';

/** Monthly Tally: one row per issue (Nov 2026 – Dec 2027), totals per paper as of today. */
export function MonthlyTally() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let on = true; setRefetching(true); setError(null);
    getTallyOverview({ data: { pass: getStaffPass() } }).then((r) => { if (on) setRows(r?.rows ?? []); })
      .catch((e) => { console.error(e); if (on) setError('The tally could not be loaded. Please try again.'); })
      .finally(() => { if (on) { setLoading(false); setRefetching(false); } });
    return () => { on = false; };
  }, [tick]);

  const act = async (month: string, fn: typeof finalizeMonth | typeof reopenMonth) => {
    setBusy(month); setArmed(null); setError(null);
    try { await fn({ data: { pass: getStaffPass(), month } }); setTick((t) => t + 1); }
    catch (e) { console.error(e); setError(`Could not update ${month}. Please try again.`); }
    finally { setBusy(null); }
  };
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });

  return (
    <div className="space-y-4">
      <article className={`border-2 border-foreground bg-card p-5 print:border-0 print:p-0 ${refetching && !loading ? 'opacity-60' : ''}`}>
        <header className="flex flex-wrap items-center gap-4 border-b-4 border-double border-foreground pb-3">
          <img src={RHSR_LOGO} alt="Red Hook Star-Revue logo" className="h-14 w-14" /><img src={VSR_LOGO} alt="The Village Star-Revue logo" className="h-14 w-14 object-contain" />
          <div className="min-w-0 flex-1">
            <h2 className="font-[family-name:var(--font-heading)] text-2xl font-semibold sm:text-3xl">Monthly Ad Tally</h2>
            <p className="text-xs text-muted-foreground">Display ads per issue, as of {today}. Open months include every TFN and contracted ad already booked.</p>
          </div>
        </header>
        {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[620px] border-collapse text-sm">
            <thead><tr><th className={th}>Issue</th><th className={`${th} text-right`}>Red Hook Star-Revue</th><th className={`${th} text-right`}>The Village Star-Revue</th><th className={th}>Status</th><th className={`${th} print:hidden`}><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {loading && [0, 1, 2, 3].map((i) => <tr key={i}><td colSpan={5} className="p-2"><div className="h-5 animate-pulse bg-muted" /><span className="sr-only">Loading</span></td></tr>)}
              {!loading && rows.map((r) => (
                <tr key={r.month}>
                  <td className={`${td} font-medium`}>{r.month}</td>
                  <td className={`${td} text-right`}>{money(r.redHook)} <span className="text-xs text-muted-foreground">({r.redHookCount} ads)</span><PerPage pages={r.redHookPages} sized={r.redHookSized} /></td>
                  <td className={`${td} text-right`}>{money(r.village)} <span className="text-xs text-muted-foreground">({r.villageCount} ads)</span><PerPage pages={r.villagePages} sized={r.villageSized} /></td>
                  <td className={td}>{r.final
                    ? <span className="inline-flex items-center gap-1 bg-foreground px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-background"><Lock className="h-3 w-3" />Final{r.finalizedAt ? ` · ${new Date(r.finalizedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</span>
                    : <span className="text-xs text-muted-foreground">Running total</span>}</td>
                  <td className={`${td} text-right print:hidden`}>
                    {r.final
                      ? <button type="button" disabled={busy === r.month} onClick={() => void act(r.month, reopenMonth)} className="inline-flex min-h-9 items-center gap-1 text-xs underline underline-offset-2 disabled:opacity-50">{busy === r.month ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlock className="h-3.5 w-3.5" />}Reopen</button>
                      : <button type="button" disabled={busy === r.month} onClick={() => (armed === r.month ? void act(r.month, finalizeMonth) : setArmed(r.month))} className={`inline-flex min-h-9 items-center gap-1 px-2 text-xs font-semibold disabled:opacity-50 ${armed === r.month ? 'bg-foreground text-background' : 'border border-foreground'}`}>{busy === r.month ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}{armed === r.month ? 'Press again to make final' : 'Make final'}</button>}
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </article>
      <button type="button" onClick={() => window.print()} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground px-4 text-sm font-semibold print:hidden"><Printer className="h-4 w-4" />Print</button>
    </div>
  );
}

/** Second line under a paper's total: pages filled and average income per ad page. */
function PerPage({ pages, sized }: { pages: number; sized: number }) {
  if (pages <= 0) return <span className="block text-[11px] text-muted-foreground">no sized ads yet</span>;
  return <span className="block text-[11px] font-semibold text-[hsl(var(--brand-blue))]">{fmtPages(pages)} pg · {money(sized / pages)}/page</span>;
}
